import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeft20Regular, Play20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useImageMetadataUpdater } from "../hooks/useImageMetadataUpdater";
import { ClientRoutes } from "../models/ClientRoutes";

const useStyles = makeStyles({
  root: {
    padding: tokens.spacingVerticalL,
    overflowY: "auto",
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  feedback: {
    marginTop: tokens.spacingVerticalL,
  },
});

export default function ImageMetadataUpdaterPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateMutation = useImageMetadataUpdater();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [feedback, setFeedback] = useState<"success" | "error">();

  const update = async () => {
    setFeedback(undefined);
    try {
      await updateMutation.mutateAsync();
      setFeedback("success");
    } catch {
      setFeedback("error");
    } finally {
      setConfirmationOpen(false);
    }
  };

  return (
    <main className={classes.root} aria-live="polite">
      <div className={classes.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={() => navigate(ClientRoutes.SystemAdmin)}
        >
          {t("imageMetadataUpdater.back")}
        </Button>
        <Text as="h1" size={600} weight="semibold">
          {t("imageMetadataUpdater.title")}
        </Text>
        <div className={classes.actions}>
          {updateMutation.isPending && (
            <Spinner size="tiny" label={t("imageMetadataUpdater.pending")} />
          )}
          <Button
            appearance="primary"
            icon={<Play20Regular />}
            disabled={updateMutation.isPending}
            onClick={() => setConfirmationOpen(true)}
          >
            {t("imageMetadataUpdater.start")}
          </Button>
        </div>
      </div>

      {feedback && (
        <MessageBar
          className={classes.feedback}
          intent={feedback === "success" ? "success" : "error"}
        >
          <MessageBarBody>
            {t(`imageMetadataUpdater.${feedback}`)}
          </MessageBarBody>
        </MessageBar>
      )}

      <Dialog
        open={confirmationOpen}
        onOpenChange={(_, data) => !data.open && setConfirmationOpen(false)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t("imageMetadataUpdater.confirm.title")}</DialogTitle>
            <DialogContent>
              {t("imageMetadataUpdater.confirm.body")}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmationOpen(false)}>
                {t("imageMetadataUpdater.cancel")}
              </Button>
              <Button
                appearance="primary"
                disabled={updateMutation.isPending}
                onClick={update}
              >
                {t("imageMetadataUpdater.confirmAction")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </main>
  );
}
